import {
  DEFAULT_PAGE_SIZE,
  DashboardTiles,
  LICENSES_SORT_BY,
  LICENSES_TABLE_COLUMNS,
  VENDORS,
} from '@common/constants';
import { ExtendedOption, IOption, OrderBy } from '@common/types';
import { TableWrapper } from '@components/partials';
import { OverlayPage } from '@components/partials/OverlayPage';
import { FilterData } from '@components/partials/TableWrapper/parts/Filter';
import { ArrowRightIcon, DashboardIcon, LightInfoIcon } from '@cloudclevr/components';
import { MenuItem } from '@components/ui/DropdownMenu';
import {
  useLicencesFiltersQuery,
  useLicencesInactiveQuery,
  useLicencesOverassignedQuery,
  useLicencesOverassignedTrendQuery,
  useLicensesOverassignedPreviewQuery,
  useRedundantDataLicencesFiltersQuery,
  useRedundantLicencesFiltersQuery,
} from '@hooks/licenses';
import { useDashboardTileSaveMutation } from '@hooks/dashboard';
import {
  LicensesFilterData,
  LicensesFiltersResponse,
  RedundantLicenseFilterResponse,
} from '@hooks/licenses/types';
import { PeopleSortBy } from '@hooks/people/types';
import { useIsDesktop } from '@hooks/utils';
import {
  LineChartTile,
  LineChartTypes,
} from '@pages/InsightsAndAnalytics/tiles/LineChartTile';
import {
  formatDate,
  formatLicensesFilter,
  getTrendData,
  getTrendLabels,
  useTableData,
} from '@utils/index';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { NumberTile } from '@pages/InsightsAndAnalytics/tiles//NumberTile';
import styles from './styles.module.css';
import { createCellValue } from './utils';
import { useNavSourceRedirect } from '@hooks/utils/dashboard';
import { useLocationsQuery } from '@hooks/locations';
import { useGroupsFilterQuery } from '@hooks/groups';
import { Location } from '@hooks/locations/types';
import { GroupsResponse } from '@hooks/groups/types';
import useDownloadCsv from '@hooks/utils/export';
import { API_ENDPOINTS } from '@api/ApiEndpoints';

const pageSize = DEFAULT_PAGE_SIZE;

const OverassignedLicensePage: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState('');
  const isDesktop = useIsDesktop();
  const redirectPath = useNavSourceRedirect(
    '/insights-and-analytics/utilisation'
  );
  const [days, setDays] = useState('30');

  const sortMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'Last 30 days',
      value: '30',
      action: () => handleDropdown('30'),
    },
    {
      id: '2',
      label: 'Last 60 days',
      value: '60',
      action: () => handleDropdown('60'),
    },
    {
      id: '3',
      label: 'Last 90 days',
      value: '90',
      action: () => handleDropdown('90'),
    },
  ];

  const handleDropdown = (value: string) => {
    setDays(value);
  };

  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [queryParams, setQueryParams] = useQueryParams({
    search: withDefault(StringParam, ''),
    sortBy: withDefault(StringParam, 'assignedto'),
    order: withDefault(StringParam, 'asc'),
    filter: withDefault(StringParam, ''),
  });

  const {
    data: licencesOverassignedData,
    isLoading: isLicencesOverassignedLoading,
  } = useLicencesOverassignedQuery({
    reasonList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => ['Inactive', 'Overassigned'].includes(filter as any))
      : [],
    locationList: queryParams.filter
      ? queryParams?.filter
        .split(',')
        .filter((filter) => locationIds.includes(filter))
      : [],
    groupList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => groupIds.includes(filter))
      : [],
    vendorList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => VENDORS.includes(filter as any))
      : [],
    licenseTypesList: formatLicensesFilter(
      queryParams?.filter,
      locationIds,
      groupIds
    ),

  });

  const {
    data: licencesInactiveData,
    isLoading: isLicencesInactiveLoading,
  } = useLicencesInactiveQuery({
    reasonList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => ['Inactive', 'Overassigned'].includes(filter as any))
      : [],
    locationList: queryParams.filter
      ? queryParams?.filter
        .split(',')
        .filter((filter) => locationIds.includes(filter))
      : [],
    groupList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => groupIds.includes(filter))
      : [],
    vendorList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => VENDORS.includes(filter as any))
      : [],
    licenseTypesList: formatLicensesFilter(
      queryParams?.filter,
      locationIds,
      groupIds
    ),
  });

  const { data: licencesTrendData, isLoading: isLicencesUtilisationLoading } =
    useLicencesOverassignedTrendQuery({
      days,
      reasonList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => ['Inactive', 'Overassigned'].includes(filter as any))
        : [],
      locationList: queryParams.filter
        ? queryParams?.filter
          .split(',')
          .filter((filter) => locationIds.includes(filter))
        : [],
      groupList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => groupIds.includes(filter))
        : [],
      vendorList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => VENDORS.includes(filter as any))
        : [],
      licenseTypeList: formatLicensesFilter(
        queryParams?.filter,
        locationIds,
        groupIds
      ),
    });

  const labels = getTrendLabels(licencesTrendData?.trend || [], +days);

  const trendData = getTrendData(licencesTrendData?.trend || [], +days);

  const query = useLicensesOverassignedPreviewQuery({
    pageSize,
    pageNumber,
    searchTerm: search,
    sortBy: queryParams.sortBy as PeopleSortBy,
    order: queryParams.order as OrderBy,
    reasonList: queryParams?.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => ['Inactive', 'Overassigned'].includes(filter as any))
      : [],
    locationList: queryParams.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => locationIds.includes(filter))
      : [],
    groupList: queryParams.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => groupIds.includes(filter))
      : [],
    vendorList: queryParams.filter
      ? queryParams.filter
        .split(',')
        .filter((filter) => VENDORS.includes(filter as any))
      : [],
    licenseTypeList: formatLicensesFilter(
      queryParams.filter,
      locationIds,
      groupIds
    ),
  });
  const getTableData = useTableData(
    query,
    LICENSES_TABLE_COLUMNS,
    createCellValue
  );

  const { data: filterData, isFetched: filterDataFetched } =
    useRedundantDataLicencesFiltersQuery();
  const { data: locationsData, isFetched: locationDataFetched } =
    useLocationsQuery();
  const { data: groupsData, isFetched: groupsDataFetched } =
    useGroupsFilterQuery();

  const propertyLabels: Record<string, string> = {
    vendor: 'Vendor',
    licensetype: 'Licence Type',
  };

  const getPropertyLabel = (property: string): string => {
    if (property === 'vendorList') return 'Vendor';
    if (property === 'licenseTypesList') return 'Licence name';
    if (property === 'reasonList') return 'Redundancy reason';
    return propertyLabels[property.toLowerCase()] || property;
  };

  const allLocationIds: string[] = [];
  const groupsIDs: string[] = [];
  const locationFilerOptions: IOption[] = [];
  const groupFilerOptions: ExtendedOption[] = [];
  const transformedFilterData: FilterData[] = [];

  if (filterDataFetched && locationDataFetched && groupsDataFetched) {
    if (filterData) {
      Object.keys(filterData).forEach((property) => {
        const filterDataArray: FilterData = {
          label: getPropertyLabel(property),
          name: property.toLowerCase(),
          options: [],
          singleSelect: false,
        };

        const propertyData =
          filterData[property as keyof RedundantLicenseFilterResponse];

          console.log()

        const items = propertyData as string[];

        items.forEach((item) => {
          const option = {
            label: item,
            value: item,
          };

          filterDataArray.options.push(option);
        });

        transformedFilterData.push(filterDataArray);
      });
    }

    if (locationsData) {
      locationsData?.forEach((location: Location) => {
        locationFilerOptions.push({ label: location.name, value: location.id });
        allLocationIds.push(location.id);
      });

      transformedFilterData.push({
        label: 'Location',
        name: 'location',
        singleSelect: false,
        options: locationFilerOptions,
      });
    }

    if (groupsData) {
      groupsData?.forEach((group: GroupsResponse) => {
        groupFilerOptions.push({
          name: group.name,
          value: group.id,
          parentId: group.parentId,
          subGroups: group.subGroups,
          id: group.id,
          label: group.name,
        });
      });

      transformedFilterData.push({
        label: 'Groups',
        name: 'groups',
        singleSelect: false,
        options: groupFilerOptions,
      });
    }
  }

  useEffect(() => {
    locationsData?.forEach((location: Location) => {
      locationFilerOptions.push({ label: location.name, value: location.id });
      allLocationIds.push(location.id);
    });
    setLocationIds(allLocationIds);
  }, [locationsData]);

  useEffect(() => {
    const nested = (element: any) => {
      element.subGroups.forEach((subElement: any) => {
        groupsIDs.push(subElement.id);
        if (subElement.subGroups && subElement.subGroups.length > 0) {
          nested(subElement);
        }
      });
    };

    groupsData?.forEach((element: any) => {
      groupsIDs.push(element.id);
      if (element.subGroups && element.subGroups.length > 0) {
        nested(element);
      }
    });

    setGroupIds(groupsIDs);
  }, [groupsData]);

  const dashboardSaveMutation = useDashboardTileSaveMutation();

  const handleItemAction = (type: DashboardTiles) => () => {
    const getParametersData = () => {
      switch (type) {
        case DashboardTiles.OverassignedLicensesTrendOverTime:
          return {
            days,
            vendorList: queryParams.filter
              ? queryParams.filter
                .split(',')
                .filter((filter) => VENDORS.includes(filter as any))
              : [],
            licenseTypeList: formatLicensesFilter(queryParams.filter),
          };
        default:
          return {};
      }
    };
    const parametersData = getParametersData();

    dashboardSaveMutation.mutate({
      parameters: JSON.stringify(parametersData),
      tileType: type,
    });
  };

  const createMenuItems = (tileType: DashboardTiles): MenuItem[] => [
    {
      id: '0',
      label: 'Add to Dashboard',
      icon: <DashboardIcon classNames="mr-1" />,
      value: 'addToDashboard',
      action: handleItemAction(tileType),
    },
  ];

  const selectedSortItem = sortMenuItems.find((item) => item.value === days);

  const legendLabels = [(selectedSortItem && selectedSortItem.label) || ''];

  const [shouldDownload, setShouldDownload] = useState(false);

  const data = useDownloadCsv({
    shouldDownload,
    endpoint: `${API_ENDPOINTS.LICENSES_OVERASSIGNED}/export`,
    params: {
      pageSize,
      pageNumber,
      searchTerm: search,
      sortBy: queryParams.sortBy as PeopleSortBy,
      order: queryParams.order as OrderBy,
      reasonList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => ['Inactive', 'Overassigned'].includes(filter as any))
        : [],
      locationList: queryParams.filter
        ? queryParams?.filter
          .split(',')
          .filter((filter) => locationIds.includes(filter))
        : [],
      groupList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => groupIds.includes(filter))
        : [],
      vendorList: queryParams?.filter
        ? queryParams.filter
          .split(',')
          .filter((filter) => VENDORS.includes(filter as any))
        : [],
      licenseTypeList: formatLicensesFilter(
        queryParams?.filter,
        locationIds,
        groupIds
      ),
    },
    filename: `Clevr360_Overassigned_licenses_${formatDate(
      new Date().toString()
    )}.csv`,
  });

  const downloadCsv = data?.refetch;

  const handleDownloadClick = () => {
    setShouldDownload(true);
    downloadCsv && downloadCsv();
    setShouldDownload(false);
  };

  return (
    <OverlayPage
      isFooterVisible={false}
      contentClassNames={''}
      path={redirectPath}
      headerTitle={`Redundant licenses`}
    >
      <div className={styles.pageGrid}>
        {isDesktop && (
          <>
            <LineChartTile
              classNames={styles.lineChartTile}
              sortData={sortMenuItems}
              compareData={undefined}
              compareLabel=""
              initialSelectedItem={selectedSortItem}
              legendLabels={legendLabels}
              dataSetColors={['#00CF6C']}
              contextData={createMenuItems(
                DashboardTiles.OverassignedLicensesTrendOverTime
              )}
              tooltipLabel={'redundant licenses'}
              headerTitle={'Redundant licenses: Trend over time'}
              isLoading={isLicencesUtilisationLoading}
              labels={labels}
              data={[trendData]}
              dataSetTypes={[LineChartTypes.Dots]}
            />

            <NumberTile
              headerTitle={'Overassigned licenses'}
              isLoading={isLicencesOverassignedLoading}
              percentageLabel={licencesOverassignedData?.percentage === 0 ? 'No change in the previous 30 days' :
                Number(licencesOverassignedData?.percentage) > 0 ? 'up from 30 days ago' : 'down from 30 days ago'}
              data={
                licencesOverassignedData
                  ? Object.values(licencesOverassignedData)
                  : [0, 0]
              }
              buttonLabel=""
              isArrowColorReversed={true}
            />
            <NumberTile
              headerTitle={'Inactive licenses'}
              isLoading={isLicencesInactiveLoading}
              percentageLabel={licencesInactiveData?.percentage === 0 ? 'No change in the previous 30 days' :
                Number(licencesInactiveData?.percentage) > 0 ? 'up from 30 days ago' : 'down from 30 days ago'}
              data={
                licencesInactiveData
                  ? Object.values(licencesInactiveData)
                  : [0, 0]
              }
              buttonLabel=""
              isArrowColorReversed={true}
            />
          </>
        )}
      </div>

      <TableWrapper
        testId='licenses-table'
        search={search}
        setSearch={setSearch}
        searchPlaceholder={'Search by licence type, name, email address'}
        columns={LICENSES_TABLE_COLUMNS}
        data={getTableData()}
        sortData={LICENSES_SORT_BY}
        filterData={transformedFilterData}
        searchKey="search"
        query={query}
        refetchQuery={query.refetch}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        setPageNumber={setPageNumber}
        isCollapsable={true}
        sliceColumns={2}
        floatingFilterButton={true}
        hasFilterBanner={false}
        searchCountStatsLabel="redundant licences"
        filterHeaderText="Filter redundant licences"
        isDownloadAvailable={true}
        handleDownload={handleDownloadClick}
        noResultTitle={
          search
            ? 'No results matching your search'
            : `You don't have any redundant licenses`
        }
        noResultSubtitle={search ? 'Try a less specific search.' : ''}
        noResultAction={
          <div className="mt-5 w-full">
            <Link
              to={'/insights-and-analytics/utilisation/license-inventory/subscriptions'}
              className={styles.noResultButton}
            >
              View your licence inventory
              <ArrowRightIcon
                color={'text-primaryBrandColor-100'}
                size="3"
                classNames="ml-2 mt-[2px]"
              />
            </Link>
          </div>
        }
      />
    </OverlayPage>
  );
};

export default OverassignedLicensePage;
